import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Minus, Plus, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const toKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Book() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [tripId, setTripId] = useState(params.get("trip") || "");
  const [priceIdx, setPriceIdx] = useState(0);
  const [date, setDate] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/trips").then((res) => {
      const bookable = res.data.filter((t) => t.prices.length > 0);
      setTrips(bookable);
      if (!tripId && bookable.length) setTripId(bookable[0].id);
    }).catch(() => toast.error("Could not load trips."));
  }, []);

  const trip = useMemo(() => trips.find((t) => t.id === tripId), [trips, tripId]);
  const price = trip?.prices[priceIdx];
  const isWholeBoat = price?.lookup_key === "trip_bluefin_boat";
  const total = price ? price.amount_gbp * (isWholeBoat ? 1 : quantity) : 0;

  const pickTrip = (id) => { setTripId(id); setPriceIdx(0); setQuantity(1); };

  const submit = async () => {
    if (!trip || !price) return toast.error("Pick a trip first.");
    if (!date) return toast.error("Pick an available date on the calendar.");
    if (!name.trim()) return toast.error("Please add your name.");
    if (!email.trim() || !email.includes("@")) return toast.error("Please add a valid email.");
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: price.lookup_key,
        quantity: isWholeBoat ? 1 : quantity,
        origin_url: window.location.origin,
        date: toKey(date),
        name: name.trim(),
        email: email.trim(),
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e, "Could not start checkout."));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="book-page">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <button data-testid="book-back-button" onClick={() => navigate("/")} className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-sky-600">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </button>
        <h1 className="mt-4 font-display text-5xl font-black uppercase tracking-tight text-slate-900" data-testid="book-headline">
          Book Your <span className="text-sky-500">Trip</span>
        </h1>
        <p className="mt-2 text-slate-500">Pick a trip, choose a live date, and pay securely online.</p>

        <div className="mt-10 grid gap-10 lg:grid-cols-5">
          <div className="space-y-8 lg:col-span-3">
            {/* Step 1: trip */}
            <div>
              <p className="section-eyebrow mb-3">1 — Choose your trip</p>
              <div className="grid gap-3 sm:grid-cols-2" data-testid="book-trip-options">
                {trips.map((t) => (
                  <button key={t.id} data-testid={`book-trip-${t.id}`} onClick={() => pickTrip(t.id)}
                    className={`rounded-xl border p-4 text-left transition-[border-color,background-color,box-shadow] duration-200 ${
                      tripId === t.id ? "border-sky-500 bg-sky-50 shadow-md shadow-sky-900/10" : "border-slate-200 bg-white hover:border-sky-300"}`}>
                    <span className="font-display text-xl font-extrabold uppercase tracking-tight text-slate-900">{t.name}</span>
                    <span className="mt-1 block text-xs text-slate-500">{t.tagline}</span>
                    <span className="mt-2 block text-sm font-bold text-sky-600">from £{Math.min(...t.prices.map((p) => p.amount_gbp))}</span>
                  </button>
                ))}
              </div>
              {trip && trip.prices.length > 1 && (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="book-price-options">
                  {trip.prices.map((p, i) => (
                    <button key={p.lookup_key} data-testid={`book-price-${p.lookup_key}`} onClick={() => { setPriceIdx(i); setQuantity(1); }}
                      className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                        priceIdx === i ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-sky-400"}`}>
                      {p.label} — £{p.amount_gbp}
                    </button>
                  ))}
                </div>
              )}
              {trip && <p className="mt-2 text-xs text-slate-400">{trip.prices[priceIdx].note}</p>}
            </div>

            {/* Step 2: date */}
            <div>
              <p className="section-eyebrow mb-3">2 — Pick a live date</p>
              <AvailabilityCalendar mode="pick" selected={date} onSelect={setDate} />
              {date && (
                <p className="mt-3 text-sm font-semibold text-sky-700" data-testid="book-selected-date">
                  Selected: {date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </p>
              )}
            </div>

            {/* Step 3: details */}
            <div>
              <p className="section-eyebrow mb-3">3 — Your details</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="book-name">Name</Label>
                  <Input id="name" data-testid="book-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="mt-1 bg-white" />
                </div>
                <div>
                  <Label htmlFor="book-email">Email</Label>
                  <Input id="email" data-testid="book-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1 bg-white" />
                </div>
              </div>
              {!isWholeBoat && trip && (
                <div className="mt-4 flex items-center gap-4">
                  <Label>Anglers</Label>
                  <div className="flex items-center gap-3 rounded-full border border-slate-200 bg-white px-2 py-1">
                    <button data-testid="book-qty-minus" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="rounded-full p-1.5 hover:bg-slate-100" aria-label="Fewer anglers">
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-6 text-center font-bold" data-testid="book-qty-value">{quantity}</span>
                    <button data-testid="book-qty-plus" onClick={() => setQuantity(Math.min(trip.max_quantity, quantity + 1))} className="rounded-full p-1.5 hover:bg-slate-100" aria-label="More anglers">
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <span className="text-xs text-slate-400">max {trip.max_quantity}</span>
                </div>
              )}
            </div>
          </div>

          {/* Summary */}
          <aside className="lg:col-span-2">
            <div className="card-clean sticky top-24 p-6" data-testid="book-summary">
              <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-slate-900">Summary</h3>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Trip</span><span className="font-semibold text-slate-900">{trip?.name || "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Option</span><span className="font-semibold text-slate-900">{price?.label || "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Date</span><span className="font-semibold text-slate-900">{date ? date.toLocaleDateString("en-GB") : "—"}</span></div>
                {!isWholeBoat && <div className="flex justify-between"><span className="text-slate-500">Anglers</span><span className="font-semibold text-slate-900">{quantity}</span></div>}
              </div>
              <div className="mt-4 flex items-baseline justify-between border-t border-slate-100 pt-4">
                <span className="text-sm font-medium text-slate-500">Total</span>
                <span className="font-display text-4xl font-black text-slate-900" data-testid="book-total">£{total}</span>
              </div>
              <Button data-testid="book-pay-button" onClick={submit} disabled={loading}
                className="btn-lift mt-6 w-full rounded-full bg-sky-500 py-6 text-base font-bold text-white hover:bg-sky-600">
                {loading ? "Starting checkout..." : <>Pay Securely <ArrowRight className="ml-2 h-5 w-5" /></>}
              </Button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-400">
                <ShieldCheck className="h-4 w-4 text-sky-500" /> Secure card payment by Stripe
              </p>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}
